import { Card, CardContent } from "@/components/ui/card";
import { Check, User, CreditCard, Zap } from "lucide-react";

const REQUIRED_FIELDS = {
  personal: ["id_number", "issuing_agency", "nationality", "marital_status", "occupation", "cellphone"],
  bank: ["bank_code", "bank_type", "bank_account", "bank_agency"],
  address: ["address_zip_code", "address_street", "address_number", "address_neighborhood", "address_city", "address_state"],
};

export function getProfileCompletion(profile) {
  if (!profile) return { filled: 0, total: 16, percent: 0, steps: {} };
  const isPix = (profile.payment_method || "pix") === "pix";
  const bankFields = isPix ? [] : REQUIRED_FIELDS.bank;
  const allFields = [...REQUIRED_FIELDS.personal, ...bankFields, ...REQUIRED_FIELDS.address];
  const filled = allFields.filter(f => profile[f] && String(profile[f]).trim()).length;
  const personalDone = REQUIRED_FIELDS.personal.every(f => profile[f] && String(profile[f]).trim());
  const bankDone = isPix ? true : REQUIRED_FIELDS.bank.every(f => profile[f] && String(profile[f]).trim());
  const addressDone = REQUIRED_FIELDS.address.every(f => profile[f] && String(profile[f]).trim());
  return {
    filled, total: allFields.length, percent: allFields.length > 0 ? Math.round((filled / allFields.length) * 100) : 0,
    steps: { personal: personalDone, bank: bankDone, address: addressDone },
    ready: personalDone && bankDone && addressDone,
  };
}

const STEPS = [
  { key: "personal", label: "Dados Pessoais", icon: User },
  { key: "bank", label: "Dados Bancários", icon: CreditCard },
  { key: "address", label: "Endereço", icon: User },
  { key: "ready", label: "Habilitado para Antecipar", icon: Zap },
];

export default function ProfileProgress({ profile }) {
  const completion = getProfileCompletion(profile);

  if (completion.ready) return null;

  return (
    <Card className="border-white/5 bg-gradient-to-r from-blue-600/10 to-purple-600/10" data-testid="profile-progress">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Complete seu perfil</p>
          <span className="text-xs font-bold text-blue-400">{completion.filled}/{completion.total} campos</span>
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
            style={{ width: `${completion.percent}%` }} />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {STEPS.map(step => {
            const done = step.key === "ready" ? completion.ready : completion.steps[step.key];
            return (
              <div key={step.key} className={`flex flex-col items-center gap-1.5 p-2 rounded-lg ${done ? "bg-emerald-500/10" : "bg-white/5"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${done ? "bg-emerald-500/20" : "bg-white/10"}`}>
                  {done ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <step.icon className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
                <span className={`text-[10px] text-center leading-tight ${done ? "text-emerald-400" : "text-muted-foreground"}`}>{step.label}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
